import * as trace from 'owa-trace';
import addAgendaItems from '../../utils/addAgendaItems';
import convertCalendarEventsToAgendaItems from '../../utils/convertCalendarEventsToAgendaItems';
import datapoints from '../../datapoints';
import getAgendaKeysRangeToRequestForScroll from '../../utils/getAgendaKeysRangeToRequestForScroll';
import getAgendaStore from '../../store/store';
import { AgendaItem } from 'mini-agenda';
import { createAppContext } from 'mini-app-context';
import { getDateString } from 'owa-datetime';
import { MAX_EVENTS_TO_FETCH } from '../../utils/agendaConstants';
import { ScrollDirectionType } from 'mini-infinite-scroll';
import { updateAnchorKeyPosition } from '../../utils/anchorKeyUtils';
import {
    CalendarFetchOrder,
    getCalendarEventsForRange,
    convertDateRangeToDisplayDateRange,
} from 'mini-calendar-events-store';

const MIN_EVENTS_TO_FETCH_ON_SCROLL = 1;

/**
 * Function which handles the load of agenda items when agenda view is scrolled.
 * @param scrollDirection Direction of scroll
 */
export default async function loadAgendaItemsForScroll(scrollDirection: ScrollDirectionType) {
    try {
        // For differential load for scroll
        const { items } = getAgendaStore();

        const scrollAppContext = createAppContext();

        const range = getAgendaKeysRangeToRequestForScroll(items, scrollDirection);
        const fetchOrder =
            scrollDirection === ScrollDirectionType.DOWN
                ? CalendarFetchOrder.Descending
                : CalendarFetchOrder.Ascending;

        // Since our range's start key is same as the key for last agenda item we have
        // that event will be returned again by the DAL which will be later deduped by us.
        // This addition is to get required number of events other than the duplicates.
        const minEventsToFetch = MIN_EVENTS_TO_FETCH_ON_SCROLL + 1;
        const maxEventsToFetch = MAX_EVENTS_TO_FETCH + 1;

        // Fetch the events from DAL
        const { events, dateRange } = await getCalendarEventsForRange(
            range,
            fetchOrder,
            minEventsToFetch /* minEventsCount */,
            maxEventsToFetch /* maxEventsCount */,
            scrollAppContext
        );

        scrollAppContext.signal(datapoints.AGENDA_VIEW_STORE.LOAD_ON_SCROLL_RESPONSE, {
            eventsLength: events.length,
            firstEventStartDate: events[0] && events[0].Start && getDateString(events[0].Start),
            lastEventStartDate:
                events[events.length - 1] &&
                events[events.length - 1].Start &&
                getDateString(events[events.length - 1].Start),
            startDateReceived: dateRange && new Date(dateRange.start).toDateString(),
            endDateReceived: dateRange && new Date(dateRange.end).toDateString(),
        });

        const { start: startDay, end: endDay } = convertDateRangeToDisplayDateRange(dateRange);
        // Transform the events to agenda items
        const agendaItems: AgendaItem[] = convertCalendarEventsToAgendaItems(
            events,
            startDay,
            endDay
        );
        const shouldAddToTopOfAgendaItems: boolean = scrollDirection === ScrollDirectionType.DOWN;

        // update anchor element position and add agenda items to store
        updateAnchorKeyPosition(false /* isOnTop */);
        addAgendaItems(agendaItems, shouldAddToTopOfAgendaItems);
    } catch (error) {
        if (error !== 'REJECT_AS_STALE') {
            trace.errorThatWillCauseAlert(`[loadAgendaItemsForScroll]: ${error}`);
        }
    }
}
